import React from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

/**
 * Global error boundary: catches unexpected runtime rendering errors
 * (e.g. DOM mutations caused by browser extensions or auto-translation)
 * and shows a graceful bilingual recovery screen instead of a black crash.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen flex items-center justify-center p-6"
          style={{ backgroundColor: 'hsl(210, 25%, 6%)' }}
          data-testid="error-boundary-fallback"
        >
          <div
            className="max-w-md w-full rounded-2xl border p-8 text-center"
            style={{
              backgroundColor: 'hsl(210, 18%, 9%)',
              borderColor: 'hsl(210, 14%, 18%)',
            }}
          >
            <div
              className="w-14 h-14 rounded-2xl mx-auto mb-5 flex items-center justify-center"
              style={{ backgroundColor: 'rgba(239, 68, 68, 0.12)' }}
            >
              <AlertTriangle size={26} style={{ color: '#F87171' }} />
            </div>
            <h1 className="text-base font-bold mb-2" style={{ color: '#E2E8F0' }}>
              Une erreur inattendue s'est produite
            </h1>
            <p className="text-xs mb-1" style={{ color: '#94A3B8' }}>
              An unexpected error occurred.
            </p>
            <p className="text-xs mb-6" style={{ color: '#64748B' }}>
              Si vous utilisez la traduction automatique du navigateur, désactivez-la pour
              cette page (l'application est déjà bilingue FR/EN).
              <br />
              If you use browser auto-translation, please disable it for this page (the app
              is already bilingual FR/EN).
            </p>
            <button
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 px-5 h-11 rounded-lg text-sm font-semibold transition-opacity duration-150 hover:opacity-90"
              style={{ backgroundColor: '#06B6D4', color: 'hsl(210, 25%, 8%)' }}
              data-testid="error-boundary-reload-button"
            >
              <RotateCcw size={15} />
              Recharger / Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
